import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { Button, LoadingAnimatedIcon, LoadingAnimatedText } from '@toolbox/components';
import { QuestionLayout } from '../components/QuestionLayout/QuestionLayout';
import { stringOrUndefined } from '../storyblok/utils/stringOrUndefined';
import { useLeadData, useWidgetContext } from '@widget/core';
import Timeslots from '@toolbox/components/Timeslots/Timeslots';
import { getSalesTimeslots } from '@widget/core/getSalesTimeslots';
import { getTimeSlotDays } from '@widget/utils/getTimeSlotDays';
import { getUserTimeZone } from '@widget/utils/getUserTimezone';
import { SALES_APPOINTMENT_TYPE } from '@widget/api/types';
import { SalesTimeslotsMutationFn } from '@widget/api/queries';

import type { TimeSlotType } from '@widget/api/types';
import type * as React from 'react';
import type { BookSalesAppointmentStepSchema, QuestionLayoutSchema } from '../storyblok/schemas';
import type { Richtext } from 'storyblok-js-client';
import type { z } from 'zod';

type StepProps = z.infer<typeof BookSalesAppointmentStepSchema>;
type QuestionLayoutProps = z.infer<typeof QuestionLayoutSchema>;

const SUBMIT_BUTTON_LABEL_DEFAULT = 'Termin vereinbaren' as const;

export const BookSalesAppointmentStep: React.FC<StepProps> = ({ ...props }) => {
  const timezone = getUserTimeZone();
  const timeslots = getSalesTimeslots();
  const { days, firstTimeslotDate } = getTimeSlotDays(timeslots, timezone);

  const questionLayoutProps = props.children[0] as QuestionLayoutProps;
  const widget = useWidgetContext();
  const { leadData } = useLeadData();

  const [date, setDate] = useState<string>(firstTimeslotDate);
  const [selectedTimeSlot, setSelectedTimeslot] = useState<null | TimeSlotType>(null);
  const submitButtonRef = useRef<null | HTMLButtonElement>(null);

  const salesTimeslotsBookMutation = useMutation({
    mutationFn: SalesTimeslotsMutationFn.main,
    onSuccess(data, variables) {
      widget.commit(
        props.id,
        {
          bookSalesAppointmentTimeSlot: data.salesforceId ? variables.salesStartTime : undefined,
          bookingType: SALES_APPOINTMENT_TYPE,
        },
        props.nextStep || undefined,
      );
    },
    onError() {
      widget.commit(
        props.id,
        { bookSalesAppointmentTimeSlot: undefined },
        props.nextStep || undefined,
      );
    },
  });

  useEffect(() => {
    if (!leadData?.id) {
      widget.skip(props.id, props.nextStep || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadData]);

  const handleDateChange = (date: string) => {
    setSelectedTimeslot(null);
    setDate(date);
  };

  const handleSubmit = async () => {
    if (!selectedTimeSlot || !leadData?.id) {
      return;
    }

    salesTimeslotsBookMutation.mutate({
      id: leadData.id,
      salesStartTime: selectedTimeSlot.isoDateTime,
    });
  };

  return (
    <QuestionLayout
      key={questionLayoutProps._uid}
      back={questionLayoutProps.back}
      submit={questionLayoutProps.submit}
      skip={questionLayoutProps.skip}
      title={questionLayoutProps.title}
      description={questionLayoutProps.description}
      backButtonText={stringOrUndefined(questionLayoutProps.backButtonText)}
      closeButtonText={stringOrUndefined(questionLayoutProps.closeButtonText)}
      submitButtonText={stringOrUndefined(questionLayoutProps.submitButtonText)}
      skipButtonText={stringOrUndefined(questionLayoutProps.skipButtonText)}
      socialProofMessage={props.socialProofMessage as Richtext}
      maxW={!questionLayoutProps.maxW ? undefined : questionLayoutProps.maxW}
      headerType={props.headerType}
    >
      {days !== undefined && !salesTimeslotsBookMutation.isPending ? (
        <div>
          {timeslots !== null && date !== null && (
            <Timeslots
              onChange={setSelectedTimeslot}
              submitButtonRef={submitButtonRef}
              selectedDate={date}
              selectedTimeslot={selectedTimeSlot}
              timeSlots={timeslots[date].slots}
              dayPickerProps={{
                days,
                onChange: handleDateChange,
              }}
              timezone={timezone}
            />
          )}
          <Box mt={12} mb={4} textAlign={'end'}>
            <Button
              refEl={submitButtonRef}
              size={'large'}
              type={'button'}
              onClick={handleSubmit}
              disabled={selectedTimeSlot === null || salesTimeslotsBookMutation.isPending}
              fullWidthMobile
            >
              {props.submitButtonLabel || SUBMIT_BUTTON_LABEL_DEFAULT}
            </Button>
          </Box>
        </div>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LoadingAnimatedIcon />
            <Box mt={3}>
              <LoadingAnimatedText text={props.loadingText} />
            </Box>
          </Box>
        </Box>
      )}
    </QuestionLayout>
  );
};
